"use client";

/**
 * The full-screen video feed: one video a screen, swiped through vertically,
 * the way every short-video app has taught people to watch.
 *
 * Opened from a row of tiles (top ads on NewForm One's Home, a request's
 * examples, an Ads Browser board, the Inspiration view) at the tile that was
 * tapped, already in place. The current video plays with sound
 * when the browser allows it, which the opening tap usually earns; when it
 * does not, the feed plays muted and the unmute control says so in words.
 * One sound setting for the whole feed, because a swipe is not a decision
 * about sound.
 *
 * NOT `VideoLightbox`, which stays for the single video with its money and
 * title beside it. That viewer is a panel over the page with a set beside the
 * player; this is the page set aside for watching, where the next video is
 * one swipe away rather than one tap on a thumbnail.
 *
 * A dialog underneath (Radix): the trap, Escape, focus restored to the tile,
 * and the page locked behind it. It does not dim, it replaces: the backdrop is
 * ink because the colour is the point, a screen for video, not a surface.
 *
 * Paging is the caller's: `onEndReached` fires a few videos short of the end
 * while `hasMore`, and the feed rests on a loader if the viewer gets there
 * first. A video whose link has expired shows a retry that calls `onRetry`,
 * where the caller refetches its query and the fresh links replace the dead.
 *
 * An item may be a still (`kind: "image"`): a static ad or a photo post sits
 * in the same scroll, fitted the same way, and simply does not play. Copy
 * comes from `labels` (English by default; NewForm One passes its catalogue).
 *
 *   <VideoFeed
 *     open={index !== null}
 *     startIndex={index ?? 0}
 *     onClose={() => setIndex(null)}
 *     title={t("title")}
 *     items={ads.map((ad) => ({ id: ad.id, src: ad.videoUrl, label: ad.name }))}
 *     renderCaption={(item) => <Caption ad={item} />}
 *     hasMore={query.hasNextPage}
 *     loadingMore={query.isFetchingNextPage}
 *     onEndReached={() => void query.fetchNextPage()}
 *     onRetry={() => void query.refetch()}
 *   />
 */

import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  ChevronDown,
  ChevronUp,
  Loader2,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import { type CSSProperties, type ReactNode, useMemo, useRef } from "react";
import { CONTROL_PRESS } from "@/components/ui/design-system/surface-styles";
import { Hint, Label } from "@/components/ui/design-system/text";
import { cn } from "@/lib/utils";
import { FeedSlide } from "./video-feed/feed-slide";
import {
  DEFAULT_VIDEO_FEED_LABELS,
  type VideoFeedLabels,
} from "./video-feed/labels";
import { useVideoFeed } from "./video-feed/use-video-feed";

export type { VideoFeedLabels } from "./video-feed/labels";

export type VideoFeedItem = {
  id: string;
  /** The file; null for a video with no file, which shows as unplayable. */
  src: string | null;
  /** A still shows as itself and never plays. Video when absent. */
  kind?: "video" | "image";
  poster?: string | null;
  /** Names the video for assistive tech, "Top ad #3". */
  label: string;
};

type VideoFeedProps<T extends VideoFeedItem> = {
  open: boolean;
  onClose: () => void;
  items: readonly T[];
  /** The video to open on, already in place. */
  startIndex: number;
  /** The dialog's accessible name. */
  title: string;
  /** Laid over the foot of each video: what it is and why it is here. */
  renderCaption?: (item: T, index: number) => ReactNode;
  hasMore?: boolean;
  loadingMore?: boolean;
  onEndReached?: () => void;
  /** Refetch the links: a video that failed, usually an expired one, retries through this. */
  onRetry?: () => void;
  /** Say so after the last video, for a feed long enough to have an end. */
  endNote?: boolean;
  /** Copy overrides, merged over the English defaults. */
  labels?: Partial<VideoFeedLabels>;
  /**
   * The token layer, from a caller whose page wears one: `/one` passes
   * `PORTAL_OVERLAY`, because a portalled panel lands outside the route's
   * `ds-next`. Its fill is overridden: the feed is always ink.
   */
  className?: string;
};

/**
 * Every round control on the feed: a dark glass disc, white glyph. A minimum
 * width rather than a size, so the sound control can grow a word.
 */
const ICON_BUTTON = cn(
  CONTROL_PRESS,
  "inline-flex h-nf-ds-12 min-w-nf-ds-12 shrink-0 items-center justify-center rounded-nf-ds-pill",
  "bg-nf-ds-ink/45 text-nf-ds-white backdrop-blur-md",
  "outline-hidden focus-visible:ring-2 focus-visible:ring-nf-ds-white",
  "[@media(hover:hover)_and_(pointer:fine)]:hover:bg-nf-ds-ink/70",
);

/** Clear of the notch and the status bar on a phone. */
const TOP_INSET: CSSProperties = {
  paddingTop: "calc(env(safe-area-inset-top) + var(--spacing-nf-ds-3))",
};

export function VideoFeed<T extends VideoFeedItem>(props: VideoFeedProps<T>) {
  const { open, onClose, title, className } = props;
  const contentRef = useRef<HTMLDivElement>(null);
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          ref={contentRef}
          // Radix traps focus and hides the page from assistive tech but does
          // not say so on the panel itself.
          aria-modal="true"
          // Radix warns when the content has neither a description nor an
          // explicit opt-out.
          aria-describedby={undefined}
          // The panel itself takes focus, not its first control, which here
          // is the sound toggle: a reflex Enter would change the sound.
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            contentRef.current?.focus({ preventScroll: true });
          }}
          className={cn(
            "fixed inset-0 z-50 h-dvh w-full overflow-hidden font-nf-ds-sans outline-hidden",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
            "duration-200 motion-reduce:animate-none",
            className,
            "bg-nf-ds-ink text-nf-ds-white",
          )}
        >
          <DialogPrimitive.Title className="sr-only">
            {title}
          </DialogPrimitive.Title>
          <FeedBody {...props} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function FeedBody<T extends VideoFeedItem>({
  open,
  onClose,
  items,
  startIndex,
  renderCaption,
  hasMore = false,
  loadingMore = false,
  onEndReached,
  onRetry,
  endNote = false,
  labels: labelOverrides,
}: VideoFeedProps<T>) {
  const labels = useMemo(
    () => ({ ...DEFAULT_VIDEO_FEED_LABELS, ...labelOverrides }),
    [labelOverrides],
  );
  const feed = useVideoFeed({
    // A still has nothing to play, so it holds no file in the pool.
    sources: items.map((item) => (item.kind === "image" ? null : item.src)),
    startIndex: Math.min(
      Math.max(startIndex, 0),
      Math.max(items.length - 1, 0),
    ),
    open,
    hasMore,
    loadingMore,
    onEndReached,
  });
  const atStart = feed.active <= 0;
  const atEnd = feed.active >= feed.stops - 1;

  return (
    <div className="relative h-full w-full">
      <div
        ref={feed.scrollerRef}
        role="feed"
        aria-busy={loadingMore}
        className="relative h-full w-full snap-y snap-mandatory overflow-y-auto overscroll-contain"
        style={{
          // The feed is swiped, not dragged by a bar; a scrollbar down the
          // right of a full-screen video is chrome over the creative.
          scrollbarWidth: "none",
          // Chrome would otherwise hold the loader in view as a page lands
          // above it, and the new videos would arrive out of sight.
          overflowAnchor: "none",
        }}
      >
        {items.map((item, index) => (
          <FeedSlide
            key={item.id}
            index={index}
            total={hasMore ? -1 : items.length}
            src={item.src}
            kind={item.kind ?? "video"}
            poster={item.poster ?? null}
            label={item.label}
            labels={labels}
            active={open && index === feed.active}
            current={feed.active}
            element={feed.elementFor(index)}
            paused={feed.paused && index === feed.active}
            onTogglePlay={feed.togglePlay}
            onRetry={onRetry}
            caption={renderCaption?.(item, index)}
          />
        ))}
        {hasMore ? (
          <div
            // `STOP_ATTRIBUTE`: the feed can rest here while a page loads.
            data-feed-stop={items.length}
            className="flex h-full w-full snap-start snap-always flex-col items-center justify-center gap-nf-ds-4"
          >
            {feed.loadStalled ? (
              <button
                type="button"
                onClick={feed.retryLoadMore}
                className={cn(
                  CONTROL_PRESS,
                  "inline-flex h-nf-ds-control items-center rounded-nf-ds-button bg-nf-ds-white px-nf-ds-5 text-nf-ds-ink",
                  "text-nf-ds-label outline-hidden focus-visible:ring-2 focus-visible:ring-nf-ds-white focus-visible:ring-offset-2 focus-visible:ring-offset-nf-ds-ink",
                )}
              >
                {labels.retry}
              </button>
            ) : (
              <>
                <Loader2
                  aria-hidden
                  className="size-nf-ds-6 animate-spin text-nf-ds-white/70 motion-reduce:animate-none"
                />
                <Hint className="text-nf-ds-white/70">
                  {labels.loadingMore}
                </Hint>
              </>
            )}
          </div>
        ) : endNote && items.length > 1 ? (
          // A short last stop: the final video lifts by this much to show
          // it, and a swipe back settles on the video again.
          <div className="flex h-nf-ds-24 snap-end items-center justify-center">
            <Hint className="text-nf-ds-white/70">{labels.end}</Hint>
          </div>
        ) : null}
      </div>

      <div
        className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between px-nf-ds-4 lg:px-nf-ds-6"
        style={TOP_INSET}
      >
        <button
          type="button"
          onClick={feed.toggleMute}
          // Muted, the visible word is the name.
          aria-label={feed.muted ? undefined : labels.mute}
          className={cn(
            ICON_BUTTON,
            "pointer-events-auto",
            // Muted, the control says what it does in words: a crossed-out
            // speaker on its own reads as a status, not as something to tap.
            feed.muted && "gap-nf-ds-2 pr-nf-ds-5 pl-nf-ds-4",
            feed.soundBlocked &&
              "bg-nf-ds-white text-nf-ds-ink [@media(hover:hover)_and_(pointer:fine)]:hover:bg-nf-ds-white/90",
          )}
        >
          {feed.muted ? (
            <>
              <VolumeX aria-hidden className="size-nf-ds-5" />
              <Label>{labels.unmute}</Label>
            </>
          ) : (
            <Volume2 aria-hidden className="size-nf-ds-5" />
          )}
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label={labels.close}
          className={cn(ICON_BUTTON, "pointer-events-auto")}
        >
          <X aria-hidden className="size-nf-ds-6" />
        </button>
      </div>

      {/* A desk has no swipe, so the step is a pair of buttons beside the
          column, and the arrow keys and j and k do the same. */}
      <div className="absolute top-1/2 right-nf-ds-6 hidden -translate-y-1/2 flex-col gap-nf-ds-3 lg:flex">
        <button
          type="button"
          onClick={() => feed.step(-1)}
          disabled={atStart}
          aria-label={labels.previous}
          className={ICON_BUTTON}
        >
          <ChevronUp aria-hidden className="size-nf-ds-6" />
        </button>
        <button
          type="button"
          onClick={() => feed.step(1)}
          disabled={atEnd}
          aria-label={labels.next}
          className={ICON_BUTTON}
        >
          <ChevronDown aria-hidden className="size-nf-ds-6" />
        </button>
      </div>
    </div>
  );
}
